import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
  Button,
  ButtonGroup,
  List,
  Tooltip,
  Link,
  Snackbar,
  Alert,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Flag as FlagIcon,
  Search as SearchIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
  AccountTree as TreeIcon,
  ViewList as ListIcon,
  ChevronRight as ChevronRightIcon,
  KeyboardArrowDown as ExpandedIcon,
  KeyboardArrowRight as CollapsedIcon,
  BugReport as DebugIcon,
  PlayArrow as PlayIcon,
  DragIndicator as DragIndicatorIcon,
  Science as ScienceIcon,
  Add as AddIcon,
  CheckCircleOutline as SavedIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Layout from '../components/Layout';
import ChainOfThought from '../components/ChainOfThought';
import PromptDebugModal from '../components/PromptDebugModal';
import ExtractionOutputRenderer from '../components/ExtractionOutputRenderer';
import api from '../api/axios';
import { useNavigate, useParams } from 'react-router-dom';

interface PromptVersionInfo {
  id: string;
  version: string;
  is_active: boolean;
  model: string;
  response_format?: 'json' | 'text';
}

interface ResponseMetadata {
  model?: string;
  reasoning_effort?: string;
  web_search_used?: boolean;
  web_searches?: Array<{ query?: string; status?: string }>;
  citations?: Array<{ type?: string; title?: string; url?: string; start_index?: number; end_index?: number }>;
  reasoning?: string[];
  usage?: { input_tokens?: number; output_tokens?: number; total_tokens?: number };
}

interface EvaluationDetail {
  press_release_id: string;
  case_name: string;
  summary: string | null;
  press_release_title: string | null;
  press_release_body: string | null;
  press_release_url: string | null;
  press_release_date: string | null;
  entity_type: string;
  prompt_version: PromptVersionInfo | null;
  available_versions?: VersionInfo[];
  extraction_run_id: string | null;
  extracted_output: ExtractedEntity[] | string;
  response_metadata: ResponseMetadata | null;
  latency_ms: number | null;
  tokens_used: number | null;
  prompt_template: string | null;
  is_correct: boolean | null;
  notes: string;
  correction: string;
  is_flagged: boolean;
  flag_notes: string;
  note_id: string | null;
  navigation: {
    current_index: number | null;
    total: number;
    prev_id: string | null;
    next_id: string | null;
  };
}

interface ExtractedEntity {
  name?: string;
  state?: string;
  type?: string;
  role?: string;
  evidence?: string;
  usd?: number;
  start_year?: number;
  end_year?: number;
  value?: string;
  abbreviation?: string;
  level?: string;
  source?: string;
  source_urls?: string[];
  // Classification outputs (fraud_type, misrep_type)
  class?: string;
  fraud_class?: string;
  misrepresented_field?: string;
  claimed_value?: string;
  true_value?: string;
  why_this_enabled_payment_or_access?: string;
  // data_signal outputs
  misrep?: string;
  fraud_type?: string;
  data_signals?: string[];
  signal_type?: string;
  // dataset_classes outputs
  signals?: Array<{
    input_signal?: string;
    canonical_signal?: string;
    dataset_classes_needed?: string[];
    notes?: string;
  }>;
  // datasets outputs
  url?: string;
  dataset_type?: string;
  description?: string;
  access_type?: string;
  vendor?: string;
  relevance_to_case?: string;
  // fraud_value outputs (new format)
  aggregate_dollar_amount?: number | null;
  // named_actors outputs
  actor_type?: string;
  // fraud_value outputs
  fraud_value?: number;
  // defrauded_parties v2 outputs
  definition?: string;
  source_type?: string;
  flag?: boolean;
  flag_why?: string | null;
  // funding_programs v2 outputs
  how?: string;
  // misrep_type v2 outputs
  defrauded_party?: string;
  funding_program?: string;
}

interface EntityType {
  value: string;
  label: string;
  sort_order: number;
  entity_format: 'text' | 'json';
}

interface SavedEntity {
  id: string;
  name: string;
  definition: string;
  metadata: Record<string, unknown>;
  is_active: boolean;
  entity_type: { slug: string };
  source_press_release_id?: string | null;
}

interface VersionInfo {
  version: string;
  status: 'draft' | 'released' | 'archived';
  is_active: boolean;
  has_extractions: boolean;
  created_at: string;
  notes?: string;
}

interface EntityState {
  data: EvaluationDetail | null;
  loading: boolean;
  isCorrect: boolean | null;
  correction: string;
  isFlagged: boolean;
  flagNotes: string;
  availableVersions: VersionInfo[];
  selectedVersion: string; // '' means active
}

interface BulkExtractionData {
  extraction_run_id: string | null;
  extracted_output: ExtractedEntity[] | string;
  response_metadata: ResponseMetadata | null;
  latency_ms: number | null;
  tokens_used: number | null;
  prompt_version: PromptVersionInfo | null;
  prompt_template: string | null;
  is_correct: boolean | null;
  notes: string;
  correction: string;
  is_flagged: boolean;
  flag_notes: string;
  note_id: string | null;
  available_versions?: VersionInfo[];
}

interface BulkEvaluationResponse {
  matter: {
    id: string;
    case_name: string;
    summary: string | null;
  };
  press_release: {
    title: string | null;
    body: string | null;
    url: string | null;
    date: string | null;
  };
  navigation: {
    current_index: number | null;
    total: number;
    prev_id: string | null;
    next_id: string | null;
  };
  extractions: Record<string, BulkExtractionData>;
  running_extractions?: Record<string, string>; // entity_type_slug -> test_run_id
}

// Decode HTML entities
const decodeHtmlEntities = (text: string): string => {
  if (!text) return '';
  const doc = new DOMParser().parseFromString(text, 'text/html');
  return doc.body.textContent || '';
};

// Clean up body text
function cleanBodyText(text: string): string {
  if (!text) return '';
  let cleaned = text
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F\u2033\u2036]/g, '"')
    .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'")
    .replace(/\.\s+"/g, '.\n\n"')
    .replace(/\.\s+(The [A-Z])/g, '.\n\n$1')
    .replace(/  +/g, ' ');
  return cleaned.trim();
}

// Highlight evidence in text
function HighlightedText({ text, evidenceQuotes }: { text: string; evidenceQuotes: string[] }) {
  const highlightedText = useMemo(() => {
    const cleanedText = cleanBodyText(text);
    if (!evidenceQuotes.length || !cleanedText) return cleanedText;

    const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const highlights: { start: number; end: number }[] = [];

    // Split evidence into fragments (handle multiple quoted sections or sentences from different parts)
    const splitIntoFragments = (quote: string): string[] => {
      // First try splitting on patterns like `" "` or `." "` that indicate separate quotes
      const quoteFragments = quote.split(/["'"]\s*["'"]/).filter(f => f.trim().length > 10);
      if (quoteFragments.length > 1) return quoteFragments;

      // Split on sentence boundaries (period followed by space and capital letter)
      // or semicolons (often used to add LLM commentary like "; public descriptions do not...")
      const sentenceFragments = quote.split(/\.\s+(?=[A-Z])|;\s+/).map(s => s.trim()).filter(f => f.length > 10);
      if (sentenceFragments.length > 1) return sentenceFragments;

      return [quote];
    };

    evidenceQuotes.forEach((quote) => {
      if (!quote || quote.length < 10) return;

      const fragments = splitIntoFragments(quote);

      fragments.forEach((fragment) => {
        let cleanedQuote = cleanBodyText(fragment)
          .replace(/^["'\s]+|["'\s]+$/g, '')
          .replace(/[.,:;!?…]+$/g, '');

        if (cleanedQuote.length < 10) return;

        const ELLIPSIS_PLACEHOLDER = '___ELLIPSIS___';
        cleanedQuote = cleanedQuote
          .replace(/\s*\.\.\.\s*/g, ELLIPSIS_PLACEHOLDER)
          .replace(/\s*…\s*/g, ELLIPSIS_PLACEHOLDER);

        const escaped = escapeRegex(cleanedQuote)
          .replace(/\s+/g, '\\s+')
          .replace(new RegExp(escapeRegex(ELLIPSIS_PLACEHOLDER), 'g'), '[\\s\\S]*?');

        try {
          const regex = new RegExp(escaped, 'gi');
          let match;
          while ((match = regex.exec(cleanedText)) !== null) {
            highlights.push({ start: match.index, end: match.index + match[0].length });
          }
        } catch {
          // Invalid regex, skip
        }
      });
    });

    if (highlights.length === 0) return cleanedText;

    highlights.sort((a, b) => a.start - b.start);
    const merged: { start: number; end: number }[] = [];
    highlights.forEach((h) => {
      if (merged.length === 0 || h.start > merged[merged.length - 1].end) {
        merged.push(h);
      } else {
        merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, h.end);
      }
    });

    const parts: React.ReactElement[] = [];
    let lastEnd = 0;
    merged.forEach((h, i) => {
      if (h.start > lastEnd) {
        parts.push(<span key={`text-${i}`}>{cleanedText.slice(lastEnd, h.start)}</span>);
      }
      parts.push(
        <mark key={`mark-${i}`} style={{ backgroundColor: '#fff59d', padding: '0 2px' }}>
          {cleanedText.slice(h.start, h.end)}
        </mark>
      );
      lastEnd = h.end;
    });
    if (lastEnd < cleanedText.length) {
      parts.push(<span key="text-end">{cleanedText.slice(lastEnd)}</span>);
    }
    return <>{parts}</>;
  }, [text, evidenceQuotes]);

  return <>{highlightedText}</>;
}

// Full names for agency/program abbreviations
const ENTITY_FULL_NAMES: Record<string, string> = {
  HHS: 'Health and Human Services',
  DOD: 'Department of Defense',
  VA: 'Veterans Affairs',
  SBA: 'Small Business Administration',
  HUD: 'Housing and Urban Development',
  CMS: 'Centers for Medicare & Medicaid Services',
  TRICARE: 'Military Health Insurance Program',
  PPP: 'Paycheck Protection Program',
  EIDL: 'Economic Injury Disaster Loan',
};

/**
 * Normalize extracted output: if it's a string that contains JSON, parse it.
 * Handles model responses that prefix JSON with "json\n" or markdown fences,
 * including truncated responses where the closing fence is missing.
 */
function normalizeOutput(output: ExtractedEntity[] | string): ExtractedEntity[] | string {
  if (typeof output !== 'string') return output;
  let cleaned = output.trim();
  // Strip markdown code fences (opening and/or closing)
  cleaned = cleaned.replace(/^```(?:json|JSON)?\s*\n?/, '');
  cleaned = cleaned.replace(/\n?\s*```$/, '');
  cleaned = cleaned.trim();
  // Try parsing if it looks like JSON (starts with [ or {)
  if (cleaned.startsWith('[') || cleaned.startsWith('{')) {
    try {
      const parsed = JSON.parse(cleaned);
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === 'object' && parsed !== null) return parsed;
    } catch {
      // Not valid JSON (possibly truncated), return fence-stripped text
    }
  }
  return cleaned;
}

function SortableEntityWrapper({ id, children }: { id: string; children: (dragHandleProps: Record<string, any>) => React.ReactNode }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ ...attributes, ...listeners })}
    </div>
  );
}

interface PromptEvaluationDetailProps {
  pressReleaseIdProp?: string;
  onNavigate?: (id: string) => void;
  onBack?: () => void;
  embedded?: boolean;
}

export default function PromptEvaluationDetail({
  pressReleaseIdProp,
  onNavigate: onNavigateProp,
  onBack,
  embedded = false,
}: PromptEvaluationDetailProps = {}) {
  const params = useParams<{ pressReleaseId: string }>();
  const pressReleaseId = pressReleaseIdProp || params.pressReleaseId;
  const navigate = useNavigate();
  const textRef = useRef<HTMLDivElement>(null);

  const [entityTypes, setEntityTypes] = useState<EntityType[]>([]);
  const [entityStates, setEntityStates] = useState<Record<string, EntityState>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const [selectedEvidence, setSelectedEvidence] = useState<string | null>(null);
  const [navigation, setNavigation] = useState<{ current_index: number | null; total: number; prev_id: string | null; next_id: string | null } | null>(null);
  const [pressReleaseInfo, setPressReleaseInfo] = useState<{ case_name: string; summary: string | null; press_release_title: string | null; press_release_body: string | null; press_release_url: string | null; press_release_date: string | null } | null>(null);
  const [isInEvalSet, setIsInEvalSet] = useState(true); // Starts true since we're viewing from eval page
  const [expandedAccordions, setExpandedAccordions] = useState<Record<string, boolean>>({});
  const [splitPercent, setSplitPercent] = useState(50); // right panel percentage
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragStartPercent = useRef(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const [misrepViewMode, setMisrepViewMode] = useState<'list' | 'tree'>('list');
  const [expandedParties, setExpandedParties] = useState<Record<string, boolean>>({});

  // Saved entities state (for entity types with has_entity_library)
  const [savedEntities, setSavedEntities] = useState<Record<string, SavedEntity[]>>({});
  const [savingEntity, setSavingEntity] = useState<string | null>(null); // tracks which entity name is being saved
  const [expandedEntity, setExpandedEntity] = useState<string | null>(null);

  // Add entity inline form state
  const [addingEntityFor, setAddingEntityFor] = useState<string | null>(null);
  const [newEntityName, setNewEntityName] = useState('');
  const [newEntityDefinition, setNewEntityDefinition] = useState('');

  // Edit saved output state
  const [editingOutputId, setEditingOutputId] = useState<string | null>(null);
  const [editingOutputText, setEditingOutputText] = useState('');


  // Debug modal state
  const [debugModalOpen, setDebugModalOpen] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [debugModalEntity, setDebugModalEntity] = useState<{
    entityType: string;
    entityTypeLabel: string;
    version: string;
    promptTemplate: string;
    output: any;
    responseMetadata: ResponseMetadata | null;
  } | null>(null);

  // Running extractions state: maps entity slug → testRunId
  const [runningExtractions, setRunningExtractions] = useState<Record<string, string>>({});

  // Drag-and-drop sensors for entity type reordering
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleEntityDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setEntityTypes((prev) => {
      const oldIndex = prev.findIndex((et) => et.value === active.id);
      const newIndex = prev.findIndex((et) => et.value === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const reordered = arrayMove(prev, oldIndex, newIndex);

      // Persist to backend
      api.post('/schemes/evaluate/reorder-entities/', {
        order: reordered.map((et) => et.value),
      }).catch((err) => console.error('Failed to save entity order:', err));

      return reordered;
    });
  }, []);

  // Test run progress notification state (persisted in localStorage)
  const ACTIVE_TEST_RUN_KEY = 'activeTestRun';
  const [activeTestRun, setActiveTestRun] = useState<{
    testRunId: string;
    versionId: string;
    version: string;
    entityType: string;
    entityTypeLabel: string;
    status: 'pending' | 'running' | 'completed' | 'failed';
    completedPressReleases: number;
    totalPressReleases: number;
  } | null>(() => {
    // Restore from localStorage on mount
    try {
      const saved = localStorage.getItem(ACTIVE_TEST_RUN_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Only restore if status is pending or running
        if (parsed.status === 'pending' || parsed.status === 'running') {
          return parsed;
        }
      }
    } catch {
      // Ignore parse errors
    }
    return null;
  });

  // Persist activeTestRun to localStorage
  useEffect(() => {
    if (activeTestRun) {
      localStorage.setItem(ACTIVE_TEST_RUN_KEY, JSON.stringify(activeTestRun));
    } else {
      localStorage.removeItem(ACTIVE_TEST_RUN_KEY);
    }
  }, [activeTestRun]);

  // Poll test run status when active
  useEffect(() => {
    if (!activeTestRun || activeTestRun.status === 'completed' || activeTestRun.status === 'failed') {
      // Clear localStorage when completed/failed
      if (activeTestRun?.status === 'completed' || activeTestRun?.status === 'failed') {
        localStorage.removeItem(ACTIVE_TEST_RUN_KEY);
      }
      return;
    }

    // Guard against undefined testRunId
    if (!activeTestRun.testRunId) {
      console.error('Invalid activeTestRun: missing testRunId');
      setActiveTestRun(null);
      localStorage.removeItem(ACTIVE_TEST_RUN_KEY);
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await api.get(`/schemes/prompts/test-runs/${activeTestRun.testRunId}/`);
        const data = response.data;

        setActiveTestRun(prev => prev ? {
          ...prev,
          status: data.status,
          completedPressReleases: data.completed_press_releases,
          totalPressReleases: data.total_press_releases,
        } : null);

        // Stop polling when completed or failed
        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(pollInterval);
          localStorage.removeItem(ACTIVE_TEST_RUN_KEY);
          // Auto-dismiss after 5 seconds for completed
          if (data.status === 'completed') {
            setTimeout(() => setActiveTestRun(null), 5000);
          }
        }
      } catch (err) {
        console.error('Failed to poll test run status:', err);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [activeTestRun?.testRunId, activeTestRun?.status]);

  // Handle release started from debug modal
  const handleReleaseStarted = (testRunInfo: {
    testRunId: string;
    versionId: string;
    version: string;
    entityType: string;
    entityTypeLabel: string;
  }) => {
    setActiveTestRun({
      ...testRunInfo,
      status: 'pending',
      completedPressReleases: 0,
      totalPressReleases: 5,
    });
  };

  // Load entity types
  useEffect(() => {
    api.get<{ entity_types: EntityType[] }>('/schemes/evaluate/entity_types/')
      .then((res) => {
        setEntityTypes(res.data.entity_types);
        // Initialize all accordions as expanded
        const initialExpanded: Record<string, boolean> = {};
        res.data.entity_types.forEach(et => { initialExpanded[et.value] = false; });
        setExpandedAccordions(initialExpanded);
      })
      .catch((err) => console.error('Failed to load entity types:', err));
  }, []);

  // Fetch all saved entities in a single request, group by entity type
  useEffect(() => {
    if (entityTypes.length === 0) return;
    api.get('/schemes/prompts/entities/', { params: { active_only: 'true', page_size: 500 } })
      .then(res => {
        const all = res.data.results || res.data || [];
        const grouped: Record<string, SavedEntity[]> = {};
        entityTypes.forEach(et => { grouped[et.value] = []; });
        all.forEach((entity: any) => {
          const slug = entity.entity_type_slug || entity.entity_type?.slug;
          if (slug && grouped[slug] !== undefined) {
            grouped[slug].push(entity);
          }
        });
        setSavedEntities(grouped);
      })
      .catch(err => console.error('Failed to load entities:', err));
  }, [entityTypes]);

  // Save an extracted item as an entity
  const handleSaveAsEntity = async (entityTypeSlug: string, name: string, metadata: Record<string, unknown> = {}, definition: string = '') => {
    if (!pressReleaseId) return;
    // Check if already saved (match on definition for text, name for json)
    if (isEntitySaved(entityTypeSlug, name, definition)) return;

    setSavingEntity(name);
    try {
      await api.post('/schemes/prompts/entities/', {
        entity_type_slug: entityTypeSlug,
        name,
        definition,
        metadata,
        source_press_release_id: pressReleaseId,
      });
      // Refresh saved entities for this type
      const res = await api.get('/schemes/prompts/entities/', { params: { entity_type: entityTypeSlug, active_only: 'false' } });
      setSavedEntities(prev => ({ ...prev, [entityTypeSlug]: res.data.results || res.data || [] }));
    } catch (err: any) {
      // Handle duplicate gracefully
      if (err?.response?.status === 400) {
        // Likely already exists, refresh
        const res = await api.get('/schemes/prompts/entities/', { params: { entity_type: entityTypeSlug, active_only: 'false' } });
        setSavedEntities(prev => ({ ...prev, [entityTypeSlug]: res.data.results || res.data || [] }));
      } else {
        console.error('Failed to save entity:', err);
      }
    } finally {
      setSavingEntity(null);
    }
  };

  // Delete a saved entity
  const handleDeleteEntity = async (entityTypeSlug: string, entityId: string) => {
    try {
      await api.delete(`/schemes/prompts/entities/${entityId}/`);
      setSavedEntities(prev => ({
        ...prev,
        [entityTypeSlug]: (prev[entityTypeSlug] || []).filter(e => e.id !== entityId),
      }));
    } catch (err) {
      console.error('Failed to delete entity:', err);
    }
  };

  // Update a saved entity's content
  const handleUpdateEntity = async (entityTypeSlug: string, entityId: string, updates: { name?: string; definition?: string; metadata?: Record<string, unknown> }) => {
    try {
      await api.patch(`/schemes/prompts/entities/${entityId}/`, updates);
      setSavedEntities(prev => ({
        ...prev,
        [entityTypeSlug]: (prev[entityTypeSlug] || []).map(e =>
          e.id === entityId ? { ...e, ...updates } : e
        ),
      }));
    } catch (err) {
      console.error('Failed to update entity:', err);
    }
  };

  // Check if an entity is already saved (matches on definition for text, name for json)
  const isEntitySaved = (entityTypeSlug: string, name: string, definition?: string): boolean => {
    const entities = savedEntities[entityTypeSlug] || [];
    if (definition) {
      return entities.some(e => e.definition === definition);
    }
    return entities.some(e => e.name === name);
  };

  // Load evaluation data for all entity types (single bulk request)
  useEffect(() => {
    if (!pressReleaseId || entityTypes.length === 0) return;

    // Set loading state immediately
    setLoading(true);

    // Initialize loading states for all entity types
    const initialStates: Record<string, EntityState> = {};
    entityTypes.forEach(et => {
      initialStates[et.value] = {
        data: null,
        loading: true,
        isCorrect: null,
        correction: '',
        isFlagged: false,
        flagNotes: '',
        availableVersions: [],
        selectedVersion: '', // empty = active version
      };
    });
    setEntityStates(initialStates);

    // Single bulk fetch for all entity types
    api.get<BulkEvaluationResponse>(`/schemes/evaluate/${pressReleaseId}/all/`)
      .then((res) => {
        const { matter: pr, press_release, navigation: nav, extractions, running_extractions: runningFromServer } = res.data;

        // Restore in-progress extractions from server
        if (runningFromServer && Object.keys(runningFromServer).length > 0) {
          setRunningExtractions(runningFromServer);
        }

        // Set press release info and navigation
        setNavigation(nav);
        setPressReleaseInfo({
          case_name: pr.case_name,
          summary: pr.summary,
          press_release_title: press_release.title,
          press_release_body: press_release.body,
          press_release_url: press_release.url,
          press_release_date: press_release.date,
        });

        // Build entity states from bulk response
        const newStates: Record<string, EntityState> = {};
        entityTypes.forEach(et => {
          const extraction = extractions[et.value];
          if (extraction) {
            // Convert bulk format to EvaluationDetail format for compatibility
            const data: EvaluationDetail = {
              press_release_id: pr.id,
              case_name: pr.case_name,
              summary: pr.summary,
              press_release_title: press_release.title,
              press_release_body: press_release.body,
              press_release_url: press_release.url,
              press_release_date: press_release.date,
              entity_type: et.value,
              prompt_version: extraction.prompt_version,
              extraction_run_id: extraction.extraction_run_id,
              extracted_output: extraction.extracted_output,
              response_metadata: extraction.response_metadata,
              latency_ms: extraction.latency_ms,
              tokens_used: extraction.tokens_used,
              prompt_template: extraction.prompt_template,
              is_correct: extraction.is_correct,
              notes: extraction.notes,
              correction: extraction.correction,
              is_flagged: extraction.is_flagged,
              flag_notes: extraction.flag_notes,
              note_id: extraction.note_id,
              navigation: nav,
            };
            newStates[et.value] = {
              data,
              loading: false,
              isCorrect: extraction.is_correct,
              correction: extraction.correction || '',
              isFlagged: extraction.is_flagged || false,
              flagNotes: extraction.flag_notes || '',
              availableVersions: extraction.available_versions || [],
              selectedVersion: '', // active version
            };
          } else {
            newStates[et.value] = {
              data: null,
              loading: false,
              isCorrect: null,
              correction: '',
              isFlagged: false,
              flagNotes: '',
              availableVersions: [],
              selectedVersion: '',
            };
          }
        });
        setEntityStates(newStates);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load evaluation data:', err);
        // Mark all as failed
        const failedStates: Record<string, EntityState> = {};
        entityTypes.forEach(et => {
          failedStates[et.value] = {
            data: null,
            loading: false,
            isCorrect: null,
            correction: '',
            isFlagged: false,
            flagNotes: '',
            availableVersions: [],
            selectedVersion: '',
          };
        });
        setEntityStates(failedStates);
        setLoading(false);
      });
  }, [pressReleaseId, entityTypes]);

  // Update entity state helper
  const updateEntityState = (entityType: string, updates: Partial<EntityState>) => {
    setEntityStates(prev => ({
      ...prev,
      [entityType]: { ...prev[entityType], ...updates }
    }));
  };

  // Fetch a single entity type with specific version
  const fetchEntityVersion = useCallback(async (entityType: string, version: string) => {
    if (!pressReleaseId) return;

    // Set loading for this entity
    updateEntityState(entityType, { loading: true });

    try {
      const params = new URLSearchParams({ entity_type: entityType });
      if (version) params.append('version', version);

      const res = await api.get<EvaluationDetail>(`/schemes/evaluate/${pressReleaseId}/?${params.toString()}`);
      const detail = res.data;

      updateEntityState(entityType, {
        data: detail,
        loading: false,
        isCorrect: detail.is_correct,
        correction: detail.correction || '',
        isFlagged: detail.is_flagged || false,
        flagNotes: detail.flag_notes || '',
        selectedVersion: version,
        // Update availableVersions if returned by API (otherwise preserve existing)
        ...(detail.available_versions ? { availableVersions: detail.available_versions } : {}),
      });
    } catch (err) {
      console.error(`Failed to load ${entityType} version ${version}:`, err);
      updateEntityState(entityType, { loading: false });
    }
  }, [pressReleaseId]);

  // Clear and rerun extraction for a single entity type
  const handleRerunExtraction = async (entityType: string) => {
    if (!pressReleaseId || runningExtractions[entityType]) return;

    // Lock immediately
    setRunningExtractions(prev => ({ ...prev, [entityType]: 'pending' }));
    try {
      // Clear existing output for this entity type
      await api.post(`/schemes/evaluate/${pressReleaseId}/clear-extractions/`, {
        entity_type: entityType,
      });
      // Reset local state to show "no extraction" while running
      updateEntityState(entityType, {
        data: {
          ...entityStates[entityType]?.data,
          extraction_run_id: null,
          extracted_output: [],
          response_metadata: null,
          is_correct: null,
          notes: '',
          correction: '',
          is_flagged: false,
          flag_notes: '',
          note_id: null,
        } as EvaluationDetail,
        isCorrect: null,
        correction: '',
        isFlagged: false,
        flagNotes: '',
      });
      // Trigger new extraction
      await handleRunExtraction(entityType);
    } catch (err) {
      console.error(`Failed to rerun extraction for ${entityType}:`, err);
    }
  };

  // Handle run extraction for an entity type with no data
  const handleRunExtraction = async (entityType: string) => {
    if (!pressReleaseId || runningExtractions[entityType]) return;

    // Lock immediately
    setRunningExtractions(prev => ({ ...prev, [entityType]: 'pending' }));
    try {
      const res = await api.post(`/schemes/evaluate/${pressReleaseId}/run-entity/`, {
        entity_type: entityType,
      });
      const testRunId = res.data.test_run_id;
      setRunningExtractions(prev => ({ ...prev, [entityType]: testRunId }));
    } catch (err) {
      console.error(`Failed to start extraction for ${entityType}:`, err);
      // Unlock on failure
      setRunningExtractions(prev => {
        const next = { ...prev };
        delete next[entityType];
        return next;
      });
    }
  };

  // Clear all extractions for this press release
  const handleClearExtractions = async () => {
    if (!pressReleaseId) return;
    setClearConfirmOpen(false);

    try {
      await api.post(`/schemes/evaluate/${pressReleaseId}/clear-extractions/`);
      setRunningExtractions({});
      // Re-fetch all entity data using the same bulk endpoint as initial load
      const res = await api.get<BulkEvaluationResponse>(`/schemes/evaluate/${pressReleaseId}/all/`);
      const { matter: pr, press_release, navigation: nav, extractions } = res.data;
      setNavigation(nav);
      const newStates: Record<string, EntityState> = {};
      entityTypes.forEach(et => {
        const extraction = extractions[et.value];
        if (extraction) {
          const data: EvaluationDetail = {
            press_release_id: pr.id,
            case_name: pr.case_name,
            summary: pr.summary,
            press_release_title: press_release.title,
            press_release_body: press_release.body,
            press_release_url: press_release.url,
            press_release_date: press_release.date,
            entity_type: et.value,
            prompt_version: extraction.prompt_version,
            extraction_run_id: extraction.extraction_run_id,
            extracted_output: extraction.extracted_output,
            response_metadata: extraction.response_metadata,
            latency_ms: extraction.latency_ms,
            tokens_used: extraction.tokens_used,
            prompt_template: extraction.prompt_template,
            is_correct: extraction.is_correct,
            notes: extraction.notes,
            correction: extraction.correction,
            is_flagged: extraction.is_flagged,
            flag_notes: extraction.flag_notes,
            note_id: extraction.note_id,
            navigation: nav,
          };
          newStates[et.value] = {
            data,
            loading: false,
            isCorrect: extraction.is_correct,
            correction: extraction.correction || '',
            isFlagged: extraction.is_flagged || false,
            flagNotes: extraction.flag_notes || '',
            availableVersions: extraction.available_versions || [],
            selectedVersion: '',
          };
        } else {
          newStates[et.value] = {
            data: null,
            loading: false,
            isCorrect: null,
            correction: '',
            isFlagged: false,
            flagNotes: '',
            availableVersions: [],
            selectedVersion: '',
          };
        }
      });
      setEntityStates(newStates);
    } catch (err) {
      console.error('Failed to clear extractions:', err);
    }
  };

  // Handle run all missing entity types
  const handleRunAllMissing = async () => {
    if (!pressReleaseId) return;

    // Find entity types with no extraction
    const missing = entityTypes.filter(et => {
      const state = entityStates[et.value];
      return state?.data && !state.data.extraction_run_id && state.data.prompt_version && !runningExtractions[et.value];
    });

    if (missing.length === 0) return;

    // Lock all immediately
    setRunningExtractions(prev => {
      const next = { ...prev };
      missing.forEach(et => { next[et.value] = 'pending'; });
      return next;
    });

    try {
      const res = await api.post(`/schemes/evaluate/${pressReleaseId}/run-all-entities/`);
      const newRunning: Record<string, string> = {};
      for (const run of res.data.runs) {
        newRunning[run.entity_type] = run.test_run_id;
      }
      setRunningExtractions(prev => ({ ...prev, ...newRunning }));
    } catch (err) {
      console.error('Failed to start run-all extractions:', err);
      // Unlock all on failure
      setRunningExtractions(prev => {
        const next = { ...prev };
        missing.forEach(et => { delete next[et.value]; });
        return next;
      });
    }
  };

  // Poll running extractions for completion
  useEffect(() => {
    const runningEntries = Object.entries(runningExtractions);
    if (runningEntries.length === 0) return;

    const pollInterval = setInterval(async () => {
      for (const [entityType, testRunId] of runningEntries) {
        try {
          const res = await api.get(`/schemes/evaluate/test-prompt/${testRunId}/`);
          const data = res.data;

          if (data.status === 'completed' || data.status === 'failed') {
            // Remove from running state
            setRunningExtractions(prev => {
              const next = { ...prev };
              delete next[entityType];
              return next;
            });

            // Refresh entity data if completed
            if (data.status === 'completed') {
              fetchEntityVersion(entityType, '');
            }
          }
        } catch (err) {
          console.error(`Failed to poll extraction status for ${entityType}:`, err);
        }
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [runningExtractions, fetchEntityVersion]);

  // Save all entities
  const handleToggleEval = async () => {
    if (!pressReleaseId) return;
    try {
      const res = await api.post(`/schemes/evaluate/${pressReleaseId}/toggle-eval/`);
      setIsInEvalSet(res.data.in_eval_set);
    } catch (err) {
      console.error('Failed to toggle eval:', err);
    }
  };

  const handleSaveAll = async (andNext = false) => {
    if (!pressReleaseId) return;
    setSaving(true);
    try {
      // Save each entity that has data
      const savePromises = Object.entries(entityStates)
        .filter(([_, state]) => state.data?.extraction_run_id)
        .map(([entityType, state]) =>
          api.post(`/schemes/evaluate/${pressReleaseId}/note/`, {
            entity_type: entityType,
            extraction_run_id: state.data?.extraction_run_id,
            is_correct: state.isCorrect,
            notes: '',
            correction: state.correction,
            is_flagged: state.isFlagged,
            flag_notes: state.flagNotes,
          })
        );
      await Promise.all(savePromises);

      if (andNext && navigation?.next_id) {
        if (onNavigateProp) {
          onNavigateProp(navigation.next_id);
        } else {
          navigate(`/schemes/evaluate/${navigation.next_id}`);
        }
      } else {
        setShowSaved(true);
      }
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setSaving(false);
    }
  };

  // Save a single entity type's correction
  const handleSaveSingle = async (entityType: string) => {
    if (!pressReleaseId) return;
    const state = entityStates[entityType];
    if (!state?.data?.extraction_run_id) return;

    setSaving(true);
    try {
      await api.post(`/schemes/evaluate/${pressReleaseId}/note/`, {
        entity_type: entityType,
        extraction_run_id: state.data.extraction_run_id,
        is_correct: state.isCorrect,
        notes: '',
        correction: state.correction,
        is_flagged: state.isFlagged,
        flag_notes: state.flagNotes,
      });
      setShowSaved(true);
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleNavigate = (targetId: string | null) => {
    if (targetId) {
      if (onNavigateProp) {
        onNavigateProp(targetId);
      } else {
        navigate(`/schemes/evaluate/${targetId}`);
      }
    }
  };

  const scrollToEvidence = useCallback((evidence: string) => {
    setSelectedEvidence(evidence);
    setTimeout(() => {
      if (!textRef.current) return;
      const marks = Array.from(textRef.current.querySelectorAll('mark'));
      if (marks.length > 0) {
        marks[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }, []);

  // Panel resize handlers
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartX.current = e.clientX;
    dragStartPercent.current = splitPercent;
  }, [splitPercent]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const containerWidth = containerRef.current?.offsetWidth || window.innerWidth;
      const deltaX = dragStartX.current - e.clientX;
      const deltaPercent = (deltaX / containerWidth) * 100;
      const newPercent = Math.max(25, Math.min(75, dragStartPercent.current + deltaPercent));
      setSplitPercent(newPercent);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const formatEntityDisplay = (entity: ExtractedEntity): string => {
    if (entity.name) return entity.name;
    // Handle fraud_type / classification outputs
    if (entity.class) return entity.class;
    if (entity.fraud_class) return entity.fraud_class;
    if (entity.state) return entity.state;
    if (entity.usd) return `$${entity.usd.toLocaleString()}`;
    if (entity.start_year || entity.end_year) {
      return `${entity.start_year || '?'} - ${entity.end_year || '?'}`;
    }
    if (entity.value) return entity.value;
    if (entity.type && !entity.name) {
      return entity.type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    }
    return JSON.stringify(entity);
  };

  const getEntityFullName = (name: string): string | null => {
    return ENTITY_FULL_NAMES[name] || null;
  };

  // Check if some entities are still loading (for inline indicator)
  const someLoading = Object.values(entityStates).some(s => s.loading);

  const Wrapper = embedded ? React.Fragment : Layout;

  if (loading) {
    return (
      <Wrapper>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Box sx={{ height: embedded ? 'calc(100vh - 160px)' : 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => onBack ? onBack() : navigate('/schemes/evaluate')}>
              <ArrowBackIcon />
            </IconButton>

            {/* Navigation */}
            {navigation && (
              <ButtonGroup size="small" variant="outlined">
                <Button
                  onClick={() => handleNavigate(navigation.prev_id)}
                  startIcon={<PrevIcon />}
                >
                  Prev
                </Button>
                <Button disabled sx={{ minWidth: 80 }}>
                  {navigation.current_index} / {navigation.total}
                </Button>
                <Button
                  onClick={() => handleNavigate(navigation.next_id)}
                  endIcon={<NextIcon />}
                >
                  Next
                </Button>
              </ButtonGroup>
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {pressReleaseInfo ? decodeHtmlEntities(pressReleaseInfo.case_name) : 'Loading...'}
              </Typography>
            </Box>

            {/* Eval Set Toggle */}
            <Tooltip title={isInEvalSet ? 'Remove from eval set' : 'Add to eval set'} arrow>
              <IconButton
                onClick={handleToggleEval}
                sx={{ color: isInEvalSet ? 'primary.main' : 'action.disabled' }}
              >
                <ScienceIcon />
              </IconButton>
            </Tooltip>

            {/* Save Buttons */}
            <Button
              variant="outlined"
              onClick={() => handleSaveAll(false)}
              disabled={saving}
              size="small"
            >
              Save All
            </Button>
            <Button
              variant="contained"
              endIcon={<NextIcon />}
              onClick={() => handleSaveAll(true)}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save All & Next'}
            </Button>
          </Box>
        </Box>

        {/* Split Panel */}
        <Box ref={containerRef} sx={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
          {/* Left Panel - Press Release Text */}
          <Box
            sx={{
              flex: `0 0 ${100 - splitPercent}%`,
              minWidth: 0,
              overflow: 'auto',
              p: 3,
              bgcolor: '#fafafa',
            }}
            ref={textRef}
          >
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">
                  {pressReleaseInfo ? decodeHtmlEntities(pressReleaseInfo.press_release_title || '') : ''}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title="Copy title & body">
                    <IconButton
                      onClick={() => {
                        const title = pressReleaseInfo?.press_release_title || '';
                        const body = cleanBodyText(pressReleaseInfo?.press_release_body || '');
                        navigator.clipboard.writeText(`${title}\n\n${body}`);
                        setShowCopied(true);
                      }}
                    >
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                  {pressReleaseInfo?.press_release_url && (
                    <Tooltip title="Open original">
                      <IconButton
                        component={Link}
                        href={pressReleaseInfo.press_release_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <OpenInNewIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
              {pressReleaseInfo?.press_release_date && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {new Date(pressReleaseInfo.press_release_date).toLocaleDateString()}
                </Typography>
              )}
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                <HighlightedText
                  text={pressReleaseInfo?.press_release_body || ''}
                  evidenceQuotes={selectedEvidence ? [selectedEvidence] : []}
                />
              </Typography>
            </Paper>
          </Box>

          {/* Draggable Divider */}
          <Box
            onMouseDown={handleDragStart}
            sx={{
              width: 6,
              cursor: 'col-resize',
              bgcolor: isDragging ? 'primary.main' : 'divider',
              '&:hover': { bgcolor: 'primary.light' },
              flexShrink: 0,
              transition: isDragging ? 'none' : 'background-color 0.2s',
            }}
          />

          {/* Right Panel - All Entity Sections */}
          <Box
            sx={{
              flex: `0 0 ${splitPercent}%`,
              minWidth: 0,
              overflow: 'auto',
              p: 2,
              bgcolor: 'background.paper',
              userSelect: isDragging ? 'none' : 'auto',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
              {(() => {
                const missingCount = entityTypes.filter(et => {
                  const state = entityStates[et.value];
                  return state?.data && !state.data.extraction_run_id && state.data.prompt_version && !runningExtractions[et.value];
                }).length;
                const anyRunning = Object.keys(runningExtractions).length > 0;
                if (missingCount === 0 && !anyRunning) return null;
                return (
                  <Tooltip title="Run extractions in parallel for all entity types that don't have outputs yet" arrow>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={anyRunning ? <CircularProgress size={12} color="inherit" /> : <PlayIcon sx={{ fontSize: '0.9rem' }} />}
                        onClick={handleRunAllMissing}
                        disabled={missingCount === 0}
                        sx={{ fontSize: '0.7rem' }}
                      >
                        {anyRunning
                          ? `Running (${Object.keys(runningExtractions).length})...`
                          : `Run All (${missingCount})`}
                      </Button>
                    </span>
                  </Tooltip>
                );
              })()}
              {entityTypes.some(et => entityStates[et.value]?.data?.extraction_run_id) && (
                <Tooltip title="Delete all extraction outputs and evaluation notes for this press release" arrow>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon sx={{ fontSize: '0.9rem' }} />}
                    onClick={() => setClearConfirmOpen(true)}
                    sx={{ fontSize: '0.7rem' }}
                  >
                    Clear All
                  </Button>
                </Tooltip>
              )}
              <Button
                size="small"
                onClick={() => {
                  // Get all entity types that have data (rendered)
                  const renderedTypes = entityTypes.filter(et => {
                    const state = entityStates[et.value];
                    return state?.data;
                  });
                  // Check if any are currently expanded
                  const anyExpanded = renderedTypes.some(et => {
                    const hasExtraction = entityStates[et.value]?.data?.extraction_run_id;
                    // Entities with extractions default to expanded, without default to collapsed
                    return hasExtraction ? expandedAccordions[et.value] !== false : expandedAccordions[et.value] === true;
                  });
                  // Toggle: if any expanded, collapse all; if all collapsed, expand all
                  const newState: Record<string, boolean> = { ...expandedAccordions };
                  renderedTypes.forEach(et => { newState[et.value] = !anyExpanded; });
                  setExpandedAccordions(newState);
                }}
                sx={{ fontSize: '0.7rem' }}
              >
                {entityTypes.filter(et => entityStates[et.value]?.data).some(et => {
                  const hasExtraction = entityStates[et.value]?.data?.extraction_run_id;
                  return hasExtraction ? expandedAccordions[et.value] !== false : expandedAccordions[et.value] === true;
                }) ? 'Collapse All' : 'Expand All'}
              </Button>
            </Box>

            {someLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Loading entities...</Typography>
              </Box>
            )}

            <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleEntityDragEnd}>
              <SortableContext items={entityTypes.map(et => et.value)} strategy={verticalListSortingStrategy}>
            {entityTypes.map((et) => {
              const state = entityStates[et.value];
              if (!state || state.loading) return null;

              const data = state.data;
              if (!data) return null;

              const isRunning = !!runningExtractions[et.value];

              // No extraction run — show simplified accordion with Run button
              if (!data.extraction_run_id) {
                return (
                  <SortableEntityWrapper key={et.value} id={et.value}>
                    {(dragHandleProps) => (
                  <Accordion
                    expanded={expandedAccordions[et.value] || false}
                    onChange={(_, isExpanded) => setExpandedAccordions(prev => ({ ...prev, [et.value]: isExpanded }))}
                    sx={{ mb: 1, opacity: isRunning ? 0.7 : 1 }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 },
                        bgcolor: 'grey.100',
                      }}
                    >
                      <DragIndicatorIcon
                        fontSize="small"
                        sx={{ color: 'text.disabled', cursor: 'grab', mr: 0.5, fontSize: '1rem' }}
                        {...dragHandleProps}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Typography variant="subtitle2" fontWeight={600} color="text.secondary">{et.label}</Typography>
                      {data.prompt_version && (
                        <Box
                          component="span"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isRunning) handleRunExtraction(et.value);
                          }}
                          sx={{
                            ml: 'auto',
                            fontSize: '0.7rem',
                            py: 0.25,
                            px: 1,
                            border: '1px solid',
                            borderColor: isRunning ? 'text.disabled' : 'success.main',
                            borderRadius: 1,
                            color: isRunning ? 'text.disabled' : 'success.main',
                            cursor: isRunning ? 'default' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            '&:hover': isRunning ? {} : { bgcolor: 'success.50' },
                          }}
                        >
                          {isRunning ? (
                            <CircularProgress size={12} color="inherit" />
                          ) : (
                            <PlayIcon sx={{ fontSize: '0.9rem' }} />
                          )}
                          {isRunning ? 'Running...' : 'Run'}
                        </Box>
                      )}
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: data.prompt_template ? 1.5 : 0 }}>
                        {data.prompt_version
                          ? 'No extraction yet for this entity type.'
                          : 'No prompt configured for this entity type.'}
                      </Typography>
                      {data.prompt_template && (
                        <Accordion
                          sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ minHeight: 32, '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center', gap: 1 } }}
                          >
                            <Typography variant="caption" color="text.secondary">Prompt</Typography>
                            <Chip
                              label="Copy"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(data.prompt_template || '');
                                setShowCopied(true);
                              }}
                              sx={{ height: 18, fontSize: '0.6rem', ml: 'auto', cursor: 'pointer' }}
                            />
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0 }}>
                            <Box
                              component="pre"
                              sx={{
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                                lineHeight: 1.6,
                                maxHeight: 300,
                                overflow: 'auto',
                                p: 1.5,
                                bgcolor: 'grey.50',
                                borderRadius: 1,
                                m: 0,
                              }}
                            >
                              {data.prompt_template}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </AccordionDetails>
                  </Accordion>
                    )}
                  </SortableEntityWrapper>
                );
              }

              return (
                <SortableEntityWrapper key={et.value} id={et.value}>
                  {(dragHandleProps) => (
                <Accordion
                  expanded={expandedAccordions[et.value] !== false}
                  onChange={(_, isExpanded) => setExpandedAccordions(prev => ({ ...prev, [et.value]: isExpanded }))}
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 },
                      bgcolor: 'grey.50',
                    }}
                  >
                    <DragIndicatorIcon
                      fontSize="small"
                      sx={{ color: 'text.disabled', cursor: 'grab', mr: 0.5, fontSize: '1rem' }}
                      {...dragHandleProps}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Typography variant="subtitle2" fontWeight={600}>{et.label}</Typography>
                    {state.isCorrect === true && (
                      <Chip
                        icon={<CheckIcon sx={{ fontSize: '0.75rem !important' }} />}
                        label="Reviewed"
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    )}
                    {state.isFlagged && <FlagIcon fontSize="small" color="primary" />}
                    {/* Debug Button - use component="span" to avoid nested button issue */}
                    <Box
                      component="span"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDebugModalEntity({
                          entityType: et.value,
                          entityTypeLabel: et.label,
                          version: data.prompt_version?.version || 'v1',
                          promptTemplate: data.prompt_template || '',
                          output: data.extracted_output,
                          responseMetadata: data.response_metadata,
                        });
                        setDebugModalOpen(true);
                      }}
                      sx={{
                        ml: 'auto',
                        fontSize: '0.7rem',
                        py: 0.25,
                        px: 1,
                        border: '1px solid',
                        borderColor: 'primary.main',
                        borderRadius: 1,
                        color: 'primary.main',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'primary.50' },
                      }}
                    >
                      Debug
                    </Box>
                    {/* Rerun Button */}
                    <Box
                      component="span"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!runningExtractions[et.value]) handleRerunExtraction(et.value);
                      }}
                      sx={{
                        fontSize: '0.7rem',
                        py: 0.25,
                        px: 1,
                        border: '1px solid',
                        borderColor: runningExtractions[et.value] ? 'text.disabled' : 'success.main',
                        borderRadius: 1,
                        color: runningExtractions[et.value] ? 'text.disabled' : 'success.main',
                        cursor: runningExtractions[et.value] ? 'default' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '&:hover': runningExtractions[et.value] ? {} : { bgcolor: 'success.50' },
                      }}
                    >
                      {runningExtractions[et.value] ? (
                        <CircularProgress size={12} color="inherit" />
                      ) : (
                        <PlayIcon sx={{ fontSize: '0.9rem' }} />
                      )}
                      {runningExtractions[et.value] ? 'Running...' : 'Rerun'}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 1 }}>
                    {/* Prompt - collapsible */}
                    <Accordion sx={{ mb: 1 }}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center', gap: 1 } }}
                      >
                        <Typography variant="caption" color="text.secondary">Prompt</Typography>
                        {data.prompt_template && (
                          <Chip
                            label="Copy"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(data.prompt_template || '');
                              setShowCopied(true);
                            }}
                            sx={{ height: 18, fontSize: '0.6rem', ml: 'auto', cursor: 'pointer' }}
                          />
                        )}
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        {data.prompt_template ? (
                          <Box
                            component="pre"
                            sx={{
                              whiteSpace: 'pre-wrap',
                              fontFamily: 'monospace',
                              bgcolor: 'grey.50',
                              p: 1,
                              borderRadius: 1,
                              maxHeight: 200,
                              overflow: 'auto',
                              fontSize: '0.7rem',
                              lineHeight: 1.4,
                              m: 0,
                            }}
                          >
                            {data.prompt_template}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">No prompt</Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>

                    {/* Output & Related Metadata */}
                    {(() => {
                      const output = normalizeOutput(data.extracted_output);
                      return (
                    <Box sx={{ mb: 1.5, p: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Output {Array.isArray(output) ? `(${output.length})` : ''}
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => {
                            const text = typeof output === 'string'
                              ? output
                              : JSON.stringify(output, null, 2);
                            navigator.clipboard.writeText(text);
                            setShowCopied(true);
                          }}
                          sx={{ fontSize: '0.65rem', py: 0, px: 0.5, minWidth: 'auto' }}
                        >
                          Copy
                        </Button>
                      </Box>

                      {typeof output === 'string' ? (
                        output.trim() ? (
                          <Box sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem', lineHeight: 1.5, maxHeight: 150, overflow: 'auto' }}>
                            {output}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">No output</Typography>
                        )
                      ) : output && output.length > 0 && et.value === 'datasets' ? (
                        // Special table rendering for datasets
                        <TableContainer sx={{ maxHeight: 400 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', py: 0.5, minWidth: 150 }}>Dataset</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', py: 0.5, minWidth: 80 }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', py: 0.5, minWidth: 80 }}>Vendor</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', py: 0.5, minWidth: 70 }}>Access</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', py: 0.5 }}>Relevance</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {output.map((dataset, i) => (
                                <TableRow key={i} sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5, verticalAlign: 'top' }}>
                                    {dataset.url ? (
                                      <Link
                                        href={dataset.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ fontWeight: 500 }}
                                      >
                                        {dataset.name}
                                      </Link>
                                    ) : (
                                      <Typography sx={{ fontWeight: 500, fontSize: '0.7rem' }}>{dataset.name}</Typography>
                                    )}
                                    {dataset.description && (
                                      <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', mt: 0.25 }}>
                                        {dataset.description}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5, verticalAlign: 'top' }}>
                                    <Chip
                                      label={dataset.dataset_type?.replace(/_/g, ' ') || '—'}
                                      size="small"
                                      sx={{ height: 18, fontSize: '0.6rem' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5, verticalAlign: 'top' }}>
                                    {dataset.vendor || '—'}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5, verticalAlign: 'top' }}>
                                    <Chip
                                      label={dataset.access_type?.replace(/_/g, ' ') || '—'}
                                      size="small"
                                      color={
                                        dataset.access_type === 'public' ? 'success' :
                                        dataset.access_type === 'purchasable' ? 'info' :
                                        dataset.access_type === 'government_confidential' ? 'warning' :
                                        dataset.access_type === 'restricted' ? 'error' : 'default'
                                      }
                                      sx={{ height: 18, fontSize: '0.6rem' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5, verticalAlign: 'top' }}>
                                    {dataset.relevance_to_case || '—'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : output && output.length > 0 && et.value === 'defrauded_parties' ? (
                        // Special rendering for defrauded_parties v2 (JSON array with name, definition, evidence, source_type, url, flag, flag_why)
                        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                          {output.map((party, i) => (
                            <Box
                              key={i}
                              onClick={() => party.evidence && scrollToEvidence(party.evidence)}
                              sx={{
                                mb: 1.5,
                                p: 1.5,
                                bgcolor: party.flag ? 'warning.50' : 'background.paper',
                                border: '1px solid',
                                borderColor: party.flag ? 'warning.main' : 'divider',
                                borderRadius: 1,
                                cursor: party.evidence ? 'pointer' : 'default',
                                '&:hover': party.evidence ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : {},
                              }}
                            >
                              {/* Party Name + Source Badge + Save Button */}
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                                <Box sx={{ flex: 1 }}>
                                  {party.url ? (
                                    <Link
                                      href={party.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'primary.main' }}
                                    >
                                      {party.name}
                                    </Link>
                                  ) : (
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                      {party.name}
                                    </Typography>
                                  )}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, alignItems: 'center' }}>
                                  {party.name && (
                                    isEntitySaved(et.value, party.name) ? (
                                      <Chip
                                        icon={<SavedIcon sx={{ fontSize: '0.75rem !important' }} />}
                                        label="Saved"
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.6rem' }}
                                      />
                                    ) : (
                                      <Chip
                                        icon={<AddIcon sx={{ fontSize: '0.75rem !important' }} />}
                                        label="Save"
                                        size="small"
                                        variant="outlined"
                                        disabled={savingEntity === party.name}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSaveAsEntity(et.value, party.name!, {
                                            definition: party.definition,
                                            source_type: party.source_type,
                                          });
                                        }}
                                        sx={{ height: 20, fontSize: '0.6rem', cursor: 'pointer' }}
                                      />
                                    )
                                  )}
                                  {party.flag && (
                                    <Chip
                                      icon={<FlagIcon sx={{ fontSize: '0.75rem !important' }} />}
                                      label="needs review"
                                      size="small"
                                      color="warning"
                                      sx={{ height: 18, fontSize: '0.6rem' }}
                                    />
                                  )}
                                  <Chip
                                    label={party.source_type === 'web' ? 'web' : 'press release'}
                                    size="small"
                                    color={party.source_type === 'web' ? 'info' : 'default'}
                                    sx={{ height: 18, fontSize: '0.6rem' }}
                                  />
                                </Box>
                              </Box>

                              {/* Definition */}
                              {party.definition && (
                                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.75, lineHeight: 1.4 }}>
                                  {party.definition}
                                </Typography>
                              )}

                              {/* Evidence */}
                              {party.evidence && (
                                <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.4 }}>
                                    "{party.evidence}"
                                  </Typography>
                                </Box>
                              )}

                              {/* Flag Why - shown when flagged */}
                              {party.flag && party.flag_why && (
                                <Box sx={{ mt: 0.75, p: 0.75, bgcolor: 'warning.100', borderRadius: 0.5 }}>
                                  <Typography sx={{ fontSize: '0.7rem', color: 'warning.dark', lineHeight: 1.4 }}>
                                    <strong>Why flagged:</strong> {party.flag_why}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      ) : output && output.length > 0 && et.value === 'funding_programs' ? (
                        // Special rendering for funding_programs v2/v3 (JSON array with name, description, how, evidence, source_type, url, defrauded_party, flag, flag_why)
                        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                          {output.map((program, i) => (
                            <Box
                              key={i}
                              onClick={() => program.evidence && scrollToEvidence(program.evidence)}
                              sx={{
                                mb: 1.5,
                                p: 1.5,
                                bgcolor: program.flag ? 'warning.50' : 'background.paper',
                                border: '1px solid',
                                borderColor: program.flag ? 'warning.main' : 'divider',
                                borderRadius: 1,
                                cursor: program.evidence ? 'pointer' : 'default',
                                '&:hover': program.evidence ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : {},
                              }}
                            >
                              {/* Defrauded Party link (from Q1) */}
                              {program.defrauded_party && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                                  <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', fontWeight: 500 }}>Q1:</Typography>
                                  <Chip
                                    label={program.defrauded_party}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                  />
                                </Box>
                              )}

                              {/* Program Name + Source Badge + Flag Badge + Save Button */}
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                                <Box sx={{ flex: 1 }}>
                                  {program.url ? (
                                    <Link
                                      href={program.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'primary.main' }}
                                    >
                                      {program.name}
                                    </Link>
                                  ) : (
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                      {program.name}
                                    </Typography>
                                  )}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, alignItems: 'center' }}>
                                  {program.name && (
                                    isEntitySaved(et.value, program.name) ? (
                                      <Chip
                                        icon={<SavedIcon sx={{ fontSize: '0.75rem !important' }} />}
                                        label="Saved"
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.6rem' }}
                                      />
                                    ) : (
                                      <Chip
                                        icon={<AddIcon sx={{ fontSize: '0.75rem !important' }} />}
                                        label="Save"
                                        size="small"
                                        variant="outlined"
                                        disabled={savingEntity === program.name}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSaveAsEntity(et.value, program.name!, {
                                            description: program.description,
                                            how: program.how,
                                            defrauded_party: program.defrauded_party,
                                          });
                                        }}
                                        sx={{ height: 20, fontSize: '0.6rem', cursor: 'pointer' }}
                                      />
                                    )
                                  )}
                                  {program.flag && (
                                    <Chip
                                      icon={<FlagIcon sx={{ fontSize: '0.75rem !important' }} />}
                                      label="needs review"
                                      size="small"
                                      color="warning"
                                      sx={{ height: 18, fontSize: '0.6rem' }}
                                    />
                                  )}
                                  <Chip
                                    label={program.source_type === 'web' ? 'web' : 'press release'}
                                    size="small"
                                    color={program.source_type === 'web' ? 'info' : 'default'}
                                    sx={{ height: 18, fontSize: '0.6rem' }}
                                  />
                                </Box>
                              </Box>

                              {/* Description (why it exists) */}
                              {program.description && (
                                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.5, lineHeight: 1.4 }}>
                                  <strong>Why:</strong> {program.description}
                                </Typography>
                              )}

                              {/* How (disbursement mechanism) */}
                              {program.how && (
                                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.5, lineHeight: 1.4 }}>
                                  <strong>How:</strong> {program.how}
                                </Typography>
                              )}

                              {/* Evidence */}
                              {program.evidence && (
                                <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.4 }}>
                                    "{program.evidence}"
                                  </Typography>
                                </Box>
                              )}

                              {/* Flag Why - shown when flagged */}
                              {program.flag && program.flag_why && (
                                <Box sx={{ mt: 0.75, p: 0.75, bgcolor: 'warning.100', borderRadius: 0.5 }}>
                                  <Typography sx={{ fontSize: '0.7rem', color: 'warning.dark', lineHeight: 1.4 }}>
                                    <strong>Why flagged:</strong> {program.flag_why}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      ) : output && output.length > 0 && et.value === 'misrep_type' ? (
                        // Special rendering for misrep_type v2/v3 with list/tree toggle
                        <Box>
                          {/* View mode toggle */}
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                            <ButtonGroup size="small" variant="outlined">
                              <Button
                                onClick={() => setMisrepViewMode('list')}
                                variant={misrepViewMode === 'list' ? 'contained' : 'outlined'}
                                sx={{ px: 1, py: 0.25, minWidth: 'auto' }}
                              >
                                <ListIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                                <Typography sx={{ fontSize: '0.7rem' }}>List</Typography>
                              </Button>
                              <Button
                                onClick={() => setMisrepViewMode('tree')}
                                variant={misrepViewMode === 'tree' ? 'contained' : 'outlined'}
                                sx={{ px: 1, py: 0.25, minWidth: 'auto' }}
                              >
                                <TreeIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                                <Typography sx={{ fontSize: '0.7rem' }}>Chain</Typography>
                              </Button>
                            </ButtonGroup>
                          </Box>

                          {misrepViewMode === 'tree' ? (
                            // Tree/Chain View - Group by defrauded_party -> funding_program -> misrep
                            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                              {(() => {
                                // Build hierarchy: party -> program -> misreps
                                const byParty: Record<string, Record<string, typeof output>> = {};
                                output.forEach((m) => {
                                  const party = m.defrauded_party || 'Unknown Party';
                                  const program = m.funding_program || 'Unknown Program';
                                  if (!byParty[party]) byParty[party] = {};
                                  if (!byParty[party][program]) byParty[party][program] = [];
                                  byParty[party][program].push(m);
                                });

                                return Object.entries(byParty).map(([party, programs]) => {
                                  const isExpanded = expandedParties[party] !== false; // Default to expanded
                                  const totalMisreps = Object.values(programs).flat().length;

                                  return (
                                  <Box key={`party-${party}`} sx={{ mb: 1 }}>
                                    {/* Q1: Defrauded Party - Collapsible */}
                                    <Box
                                      onClick={() => setExpandedParties(prev => ({ ...prev, [party]: !isExpanded }))}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        py: 0.75,
                                        px: 1,
                                        bgcolor: 'primary.50',
                                        borderRadius: 1,
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: 'primary.100' },
                                      }}
                                    >
                                      {isExpanded ? (
                                        <ExpandedIcon sx={{ fontSize: '1.2rem', color: 'primary.main' }} />
                                      ) : (
                                        <CollapsedIcon sx={{ fontSize: '1.2rem', color: 'primary.main' }} />
                                      )}
                                      <Chip label="Q1" size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />
                                      <Typography sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{party}</Typography>
                                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                        ({totalMisreps} misrep{totalMisreps !== 1 ? 's' : ''})
                                      </Typography>
                                    </Box>

                                    {/* Q2: Funding Programs - Only show when expanded */}
                                    {isExpanded && Object.entries(programs).map(([program, misreps]) => (
                                      <Box key={`program-${party}-${program}`} sx={{ ml: 2, mt: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1, bgcolor: 'secondary.50', borderRadius: 1 }}>
                                          <Box sx={{ width: 16, borderLeft: '2px solid', borderColor: 'primary.main', height: 16 }} />
                                          <Chip label="Q2" size="small" color="secondary" sx={{ height: 18, fontSize: '0.65rem' }} />
                                          <Typography sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{program}</Typography>
                                          <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                            ({misreps.length})
                                          </Typography>
                                        </Box>

                                        {/* Q3: Misrepresentations */}
                                        {misreps.map((misrep, idx) => (
                                          <Box
                                            key={`misrep-${party}-${program}-${idx}`}
                                            onClick={() => misrep.evidence && scrollToEvidence(misrep.evidence)}
                                            sx={{
                                              ml: 2,
                                              mt: 0.5,
                                              py: 0.75,
                                              px: 1,
                                              bgcolor: misrep.flag ? 'warning.50' : 'grey.50',
                                              border: '1px solid',
                                              borderColor: misrep.flag ? 'warning.main' : 'grey.200',
                                              borderRadius: 1,
                                              cursor: misrep.evidence ? 'pointer' : 'default',
                                              '&:hover': misrep.evidence ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : {},
                                            }}
                                          >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                              <Box sx={{ width: 16, borderLeft: '2px solid', borderColor: 'secondary.main', height: 12 }} />
                                              <Chip label="Q3" size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: 'grey.300' }} />
                                              <Typography sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                                                {misrep.name}
                                              </Typography>
                                              {misrep.flag && (
                                                <Chip
                                                  icon={<FlagIcon sx={{ fontSize: '0.65rem !important' }} />}
                                                  label="review"
                                                  size="small"
                                                  color="warning"
                                                  sx={{ height: 16, fontSize: '0.55rem' }}
                                                />
                                              )}
                                            </Box>
                                            {misrep.description && (
                                              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', ml: 4, mt: 0.25 }}>
                                                {misrep.description}
                                              </Typography>
                                            )}
                                            {misrep.evidence && (
                                              <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', fontStyle: 'italic', ml: 4, mt: 0.25 }}>
                                                "{misrep.evidence.length > 80 ? misrep.evidence.slice(0, 80) + '...' : misrep.evidence}"
                                              </Typography>
                                            )}
                                          </Box>
                                        ))}
                                      </Box>
                                    ))}
                                  </Box>
                                );
                                })}
                              )()}
                            </Box>
                          ) : (
                            // List View (original)
                            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                              {output.map((misrep, i) => (
                                <Box
                                  key={i}
                                  onClick={() => misrep.evidence && scrollToEvidence(misrep.evidence)}
                                  sx={{
                                    mb: 1.5,
                                    p: 1.5,
                                    bgcolor: misrep.flag ? 'warning.50' : 'background.paper',
                                    border: '1px solid',
                                    borderColor: misrep.flag ? 'warning.main' : 'divider',
                                    borderRadius: 1,
                                    cursor: misrep.evidence ? 'pointer' : 'default',
                                    '&:hover': misrep.evidence ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : {},
                                  }}
                                >
                                  {/* Chain: Defrauded Party / Funding Program (from Q1/Q2) */}
                                  {(misrep.defrauded_party || misrep.funding_program) && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
                                      {misrep.defrauded_party && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', fontWeight: 500 }}>Q1:</Typography>
                                          <Chip
                                            label={misrep.defrauded_party}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            sx={{ height: 20, fontSize: '0.65rem' }}
                                          />
                                        </Box>
                                      )}
                                      {misrep.funding_program && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', fontWeight: 500 }}>Q2:</Typography>
                                          <Chip
                                            label={misrep.funding_program}
                                            size="small"
                                            variant="outlined"
                                            color="secondary"
                                            sx={{ height: 20, fontSize: '0.65rem' }}
                                          />
                                        </Box>
                                      )}
                                    </Box>
                                  )}

                                  {/* Misrep Name + Source Badge + Flag Badge */}
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                                    <Box sx={{ flex: 1 }}>
                                      {misrep.url ? (
                                        <Link
                                          href={misrep.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'primary.main' }}
                                        >
                                          {misrep.name}
                                        </Link>
                                      ) : (
                                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                          {misrep.name}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                      {misrep.flag && (
                                        <Chip
                                          icon={<FlagIcon sx={{ fontSize: '0.75rem !important' }} />}
                                          label="needs review"
                                          size="small"
                                          color="warning"
                                          sx={{ height: 18, fontSize: '0.6rem' }}
                                        />
                                      )}
                                      <Chip
                                        label={misrep.source_type === 'web' ? 'web' : 'press release'}
                                        size="small"
                                        color={misrep.source_type === 'web' ? 'info' : 'default'}
                                        sx={{ height: 18, fontSize: '0.6rem' }}
                                      />
                                    </Box>
                                  </Box>

                                  {/* Description (how lie enabled payment) */}
                                  {misrep.description && (
                                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.5, lineHeight: 1.4 }}>
                                      {misrep.description}
                                    </Typography>
                                  )}

                                  {/* Evidence */}
                                  {misrep.evidence && (
                                    <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.4 }}>
                                        "{misrep.evidence}"
                                      </Typography>
                                    </Box>
                                  )}

                                  {/* Flag Why - shown when flagged */}
                                  {misrep.flag && misrep.flag_why && (
                                    <Box sx={{ mt: 0.75, p: 0.75, bgcolor: 'warning.100', borderRadius: 0.5 }}>
                                      <Typography sx={{ fontSize: '0.7rem', color: 'warning.dark', lineHeight: 1.4 }}>
                                        <strong>Why flagged:</strong> {misrep.flag_why}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      ) : output && output.length > 0 && et.value === 'funding_programs_and_misrepresentations' ? (
                        // Hierarchical rendering for combined funding programs + misrepresentations
                        <ExtractionOutputRenderer
                          entityType={et.value}
                          output={output}
                          maxHeight={500}
                          onEvidenceClick={scrollToEvidence}
                        />
                      ) : output && output.length > 0 ? (
                        // Check if this is classification output (has class, fraud_class, misrep, or data_signals)
                        output[0]?.class || output[0]?.fraud_class || output[0]?.misrep || output[0]?.data_signals || output[0]?.signals || output[0]?.signal_type || output[0]?.actor_type || output[0]?.fraud_value !== undefined || 'aggregate_dollar_amount' in (output[0] || {}) ? (
                          <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
                            {output.map((entity, i) => {
                              // Define key order for display based on output type
                              let keyOrder: string[];
                              if (entity.signals) {
                                // dataset_classes output
                                keyOrder = ['misrep', 'fraud_type', 'signals'];
                              } else if (entity.data_signals) {
                                keyOrder = ['misrep', 'fraud_type', 'data_signals'];
                              } else if (entity.signal_type) {
                                keyOrder = ['signal_type', 'evidence'];
                              } else if (entity.actor_type) {
                                keyOrder = ['name', 'actor_type', 'evidence', 'source'];
                              } else if (entity.fraud_value !== undefined) {
                                keyOrder = ['misrepresentation_field', 'fraud_class', 'fraud_value'];
                              } else if ('aggregate_dollar_amount' in entity) {
                                // New fraud_value format
                                keyOrder = ['misrepresented_field', 'aggregate_dollar_amount', 'evidence', 'source'];
                              } else {
                                keyOrder = ['class', 'fraud_class', 'misrepresented_field', 'claimed_value', 'true_value', 'why_this_enabled_payment_or_access', 'evidence'];
                              }
                              const orderedKeys = keyOrder.filter(k => k in entity);
                              // Add any remaining keys not in the order
                              Object.keys(entity).forEach(k => { if (!orderedKeys.includes(k)) orderedKeys.push(k); });

                              // Format value for display
                              const formatValue = (key: string, value: unknown): React.ReactNode => {
                                if (key === 'data_signals' && Array.isArray(value)) {
                                  return (value as string[]).join(', ');
                                }
                                if (key === 'fraud_value' && typeof value === 'number') {
                                  return `$${value.toLocaleString()}`;
                                }
                                if (key === 'aggregate_dollar_amount') {
                                  if (value === null) return <Chip label="null" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />;
                                  return <Chip label={`$${Number(value).toLocaleString()}`} color="success" size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }} />;
                                }
                                if (key === 'source') {
                                  return <Chip label={String(value).replace(/_/g, ' ')} color={value === 'web' ? 'info' : 'default'} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />;
                                }
                                if (key === 'signals' && Array.isArray(value)) {
                                  // Render nested signals for dataset_classes
                                  return (
                                    <Box sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid', borderColor: 'primary.light' }}>
                                      {(value as Array<{ input_signal?: string; canonical_signal?: string; dataset_classes_needed?: string[]; notes?: string }>).map((signal, si) => (
                                        <Box key={si} sx={{ mb: 1, fontSize: '0.7rem' }}>
                                          <Box><strong>canonical_signal:</strong> {signal.canonical_signal || signal.input_signal}</Box>
                                          {signal.dataset_classes_needed && (
                                            <Box><strong>dataset_classes_needed:</strong> {signal.dataset_classes_needed.join(', ')}</Box>
                                          )}
                                          {signal.notes && <Box><strong>notes:</strong> {signal.notes}</Box>}
                                        </Box>
                                      ))}
                                    </Box>
                                  );
                                }
                                return String(value);
                              };

                              return (
                                <Box
                                  key={i}
                                  onClick={() => entity.evidence && scrollToEvidence(entity.evidence)}
                                  sx={{
                                    mb: 1,
                                    p: 1,
                                    bgcolor: 'grey.50',
                                    borderRadius: 1,
                                    fontSize: '0.75rem',
                                    cursor: entity.evidence ? 'pointer' : 'default',
                                    '&:hover': entity.evidence ? { bgcolor: 'grey.100' } : {},
                                  }}
                                >
                                  {orderedKeys.map((key) => (
                                    <Box key={key} sx={{ mb: 0.25 }}>
                                      <strong>{key}:</strong> {formatValue(key, entity[key as keyof typeof entity])}
                                    </Box>
                                  ))}
                                </Box>
                              );
                            })}
                          </Box>
                        ) : (
                          <List dense disablePadding sx={{ maxHeight: 150, overflow: 'auto' }}>
                            {output.map((entity, i) => {
                              const isFromWeb = entity.source === 'web_search' || entity.source === 'web';
                              return (
                                <Box
                                  key={i}
                                  onClick={() => entity.evidence && !isFromWeb && scrollToEvidence(entity.evidence)}
                                  sx={{
                                    py: 0.5,
                                    cursor: entity.evidence && !isFromWeb ? 'pointer' : 'default',
                                    '&:hover': entity.evidence && !isFromWeb ? { bgcolor: 'action.hover' } : {},
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {(() => {
                                      const display = formatEntityDisplay(entity);
                                      const fullName = entity.name ? getEntityFullName(entity.name) : null;
                                      return fullName ? (
                                        <Tooltip title={fullName} arrow placement="top">
                                          <Typography variant="body2" fontSize="0.8rem" fontWeight={500} sx={{ cursor: 'help' }}>
                                            {display}
                                          </Typography>
                                        </Tooltip>
                                      ) : (
                                        <Typography variant="body2" fontSize="0.8rem" fontWeight={500}>
                                          {display}
                                        </Typography>
                                      );
                                    })()}
                                    {isFromWeb && <Chip label="web" size="small" color="info" sx={{ height: 14, fontSize: '0.6rem' }} />}
                                  </Box>
                                </Box>
                              );
                            })}
                          </List>
                        )
                      ) : output && typeof output === 'object' && !Array.isArray(output) ? (
                        // Handle single object outputs (e.g., external_surfacing)
                        (() => {
                          const objOutput = output as unknown as Record<string, unknown>;
                          // Order keys with surfacing_path first
                          const orderedKeys = ['surfacing_path', 'rationale', ...Object.keys(objOutput).filter(k => k !== 'surfacing_path' && k !== 'rationale')];
                          return (
                            <Box sx={{ fontSize: '0.8rem', lineHeight: 1.6 }}>
                              {orderedKeys.filter(key => key in objOutput).map((key) => (
                                <Box key={key} sx={{ mb: 1 }}>
                                  <Typography component="span" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'primary.main' }}>
                                    {key.replace(/_/g, ' ')}:
                                  </Typography>
                                  {key === 'surfacing_path' ? (
                                    <Chip
                                      label={String(objOutput[key]).replace(/_/g, ' ')}
                                      size="small"
                                      color={
                                        objOutput[key] === 'qui_tam' ? 'success' :
                                        objOutput[key] === 'government_partnership' ? 'warning' :
                                        objOutput[key] === 'industry_partnership' ? 'info' : 'default'
                                      }
                                      sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                    />
                                  ) : (
                                    <Typography component="span" sx={{ ml: 1, fontSize: '0.75rem' }}>
                                      {String(objOutput[key])}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          );
                        })()
                      ) : (
                        <Typography variant="caption" color="text.secondary">No entities extracted</Typography>
                      )}

                      {/* Chain of Thought - inside output box */}
                      {data.response_metadata?.reasoning && data.response_metadata.reasoning.length > 0 && (
                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                          <ChainOfThought steps={data.response_metadata.reasoning} defaultExpanded={false} />
                        </Box>
                      )}

                      {/* Web Searches - inside output box */}
                      {data.response_metadata?.web_search_used && (
                        <Box
                          sx={{
                            mt: 1,
                            pt: 1,
                            borderTop: data.response_metadata?.reasoning?.length ? 'none' : '1px dashed',
                            borderColor: 'divider',
                          }}
                        >
                          <Accordion
                            defaultExpanded={false}
                            disableGutters
                            sx={{ boxShadow: 'none', '&:before': { display: 'none' }, bgcolor: 'transparent', m: '0 !important' }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}
                              sx={{ minHeight: '18px !important', p: 0, flexDirection: 'row-reverse', gap: 0.5, '& .MuiAccordionSummary-content': { m: '0 !important', alignItems: 'center', gap: 0.5 }, '& .MuiAccordionSummary-expandIconWrapper': { mr: 0 } }}
                            >
                              <SearchIcon sx={{ fontSize: 14 }} color="info" />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                Web Search {data.response_metadata.web_searches?.length
                                  ? `(${data.response_metadata.web_searches.length})`
                                  : ''}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0, pl: 2.5, m: 0 }}>
                              {data.response_metadata.web_searches && data.response_metadata.web_searches.length > 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                  {data.response_metadata.web_searches.map((search, idx) => (
                                    <Typography key={idx} sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.4 }}>
                                      • {search.query}
                                    </Typography>
                                  ))}
                                </Box>
                              ) : (
                                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                  Model used web search
                                </Typography>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        </Box>
                      )}

                      {/* Citations - inside output box */}
                      {data.response_metadata?.citations && data.response_metadata.citations.length > 0 && (() => {
                        const uniqueCitations = data.response_metadata.citations.filter(
                          (citation, idx, arr) => arr.findIndex(c => c.url === citation.url) === idx
                        );
                        return (
                          <Accordion
                            defaultExpanded={false}
                            disableGutters
                            sx={{ boxShadow: 'none', '&:before': { display: 'none' }, bgcolor: 'transparent', m: '0 !important', mt: '4px !important' }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}
                              sx={{ minHeight: '18px !important', p: 0, flexDirection: 'row-reverse', gap: 0.5, '& .MuiAccordionSummary-content': { m: '0 !important', alignItems: 'center', gap: 0.5 }, '& .MuiAccordionSummary-expandIconWrapper': { mr: 0 } }}
                            >
                              <LinkIcon sx={{ fontSize: 14 }} color="secondary" />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                Citations ({uniqueCitations.length})
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0, pl: 2.5, m: 0 }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                {uniqueCitations.map((citation, idx) => (
                                  <Typography
                                    key={idx}
                                    component="a"
                                    href={citation.url || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      color: 'primary.main',
                                      textDecoration: 'none',
                                      fontSize: '0.7rem',
                                      lineHeight: 1.4,
                                      '&:hover': { textDecoration: 'underline' },
                                    }}
                                  >
                                    • {citation.title || citation.url || 'Unknown source'}
                                  </Typography>
                                ))}
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        );
                      })()}

                      {/* Latency & Tokens - small muted text */}
                      {(data.latency_ms || data.tokens_used) && (
                        <Box sx={{ mt: 1, pt: 0.5, borderTop: '1px dashed', borderColor: 'divider', display: 'flex', gap: 2 }}>
                          {data.latency_ms && (
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                              {(data.latency_ms / 1000).toFixed(1)}s
                            </Typography>
                          )}
                          {data.tokens_used && (
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                              {data.tokens_used >= 1000 ? `${(data.tokens_used / 1000).toFixed(1)}k` : data.tokens_used} tokens
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                      );
                    })()}

                    {/* Saved Outputs for this case */}
                    {(() => {
                      const caseOutputs = (savedEntities[et.value] || []).filter(e => e.source_press_release_id === pressReleaseId);
                      return caseOutputs.length > 0 ? (
                        <Box sx={{ mt: 1.5 }}>
                          <Typography variant="caption" color="success.dark" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                            Saved Outputs ({caseOutputs.length})
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {caseOutputs.map((entity) => (
                              <Box
                                key={entity.id}
                                sx={{
                                  p: 1.5,
                                  bgcolor: 'success.50',
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'success.main',
                                }}
                              >
                                {editingOutputId === entity.id ? (
                                  /* Editing mode */
                                  <Box>
                                    {et.entity_format === 'json' && (
                                      <TextField
                                        size="small"
                                        fullWidth
                                        label="Name"
                                        defaultValue={entity.name}
                                        onChange={(e) => {
                                          // Store name in a data attribute for save
                                          (e.target as any)._editName = e.target.value;
                                        }}
                                        inputRef={(el) => { if (el) (el as any)._entityId = entity.id; }}
                                        sx={{ mb: 1, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                                      />
                                    )}
                                    <TextField
                                      size="small"
                                      fullWidth
                                      multiline
                                      minRows={2}
                                      maxRows={12}
                                      value={editingOutputText}
                                      onChange={(e) => setEditingOutputText(e.target.value)}
                                      sx={{
                                        mb: 1,
                                        '& .MuiInputBase-input': {
                                          fontFamily: et.entity_format === 'json' ? 'monospace' : 'inherit',
                                          fontSize: '0.8rem',
                                        },
                                      }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                      <Button
                                        size="small"
                                        onClick={() => setEditingOutputId(null)}
                                        sx={{ fontSize: '0.75rem' }}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        variant="contained"
                                        size="small"
                                        onClick={async () => {
                                          if (et.entity_format === 'json') {
                                            let metadata = {};
                                            try { metadata = JSON.parse(editingOutputText); } catch { /* keep empty */ }
                                            await handleUpdateEntity(et.value, entity.id, { metadata, definition: editingOutputText });
                                          } else {
                                            const name = editingOutputText.slice(0, 80);
                                            await handleUpdateEntity(et.value, entity.id, { name, definition: editingOutputText });
                                          }
                                          setEditingOutputId(null);
                                        }}
                                        sx={{ fontSize: '0.75rem' }}
                                      >
                                        Save
                                      </Button>
                                    </Box>
                                  </Box>
                                ) : (
                                  /* Display mode */
                                  <Box>
                                    {et.entity_format === 'json' ? (
                                      <Box>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>{entity.name}</Typography>
                                        <Box component="pre" sx={{ fontSize: '0.7rem', fontFamily: 'monospace', m: 0, mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', bgcolor: 'background.paper', p: 1, borderRadius: 0.5 }}>
                                          {JSON.stringify(entity.metadata, null, 2)}
                                        </Box>
                                      </Box>
                                    ) : (
                                      <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {entity.definition || entity.name}
                                      </Typography>
                                    )}
                                    <Box sx={{ display: 'flex', gap: 0.5, mt: 1, justifyContent: 'flex-end' }}>
                                      <Button
                                        size="small"
                                        startIcon={<EditIcon sx={{ fontSize: '0.8rem !important' }} />}
                                        onClick={() => {
                                          setEditingOutputId(entity.id);
                                          setEditingOutputText(
                                            et.entity_format === 'json'
                                              ? JSON.stringify(entity.metadata, null, 2)
                                              : (entity.definition || entity.name)
                                          );
                                        }}
                                        sx={{ fontSize: '0.7rem', color: 'text.secondary', minWidth: 'auto' }}
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                        size="small"
                                        startIcon={<DeleteIcon sx={{ fontSize: '0.8rem !important' }} />}
                                        onClick={() => handleDeleteEntity(et.value, entity.id)}
                                        sx={{ fontSize: '0.7rem', color: 'error.main', minWidth: 'auto' }}
                                      >
                                        Delete
                                      </Button>
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      ) : null;
                    })()}

                    {/* Add Output inline form */}
                    {addingEntityFor === et.value ? (
                      <Box sx={{ mt: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                          Add Output
                        </Typography>
                        {et.entity_format === 'json' && (
                          <TextField
                            size="small"
                            fullWidth
                            label="Name"
                            value={newEntityName}
                            onChange={(e) => setNewEntityName(e.target.value)}
                            sx={{ mb: 1, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                          />
                        )}
                        <TextField
                          size="small"
                          fullWidth
                          multiline
                          minRows={2}
                          maxRows={8}
                          label={et.entity_format === 'json' ? 'Metadata (JSON)' : 'Content'}
                          value={newEntityDefinition}
                          onChange={(e) => setNewEntityDefinition(e.target.value)}
                          placeholder={et.entity_format === 'json' ? '{"key": "value"}' : 'Type the entity content...'}
                          sx={{
                            mb: 1,
                            '& .MuiInputBase-input': {
                              fontFamily: et.entity_format === 'json' ? 'monospace' : 'inherit',
                              fontSize: '0.8rem',
                            },
                          }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            onClick={() => {
                              setAddingEntityFor(null);
                              setNewEntityName('');
                              setNewEntityDefinition('');
                            }}
                            sx={{ fontSize: '0.75rem' }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            disabled={!newEntityDefinition.trim() || savingEntity !== null}
                            onClick={async () => {
                              if (et.entity_format === 'json') {
                                let metadata = {};
                                try { metadata = JSON.parse(newEntityDefinition); } catch { /* use empty */ }
                                const name = newEntityName.trim() || (metadata as any).name || newEntityDefinition.slice(0, 80);
                                await handleSaveAsEntity(et.value, name, metadata, '');
                              } else {
                                const name = newEntityName.trim() || newEntityDefinition.slice(0, 80);
                                await handleSaveAsEntity(et.value, name, {}, newEntityDefinition);
                              }
                              setAddingEntityFor(null);
                              setNewEntityName('');
                              setNewEntityDefinition('');
                            }}
                            sx={{ fontSize: '0.75rem' }}
                          >
                            Save
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          startIcon={<AddIcon sx={{ fontSize: '0.9rem' }} />}
                          onClick={() => setAddingEntityFor(et.value)}
                          sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
                        >
                          Add Output
                        </Button>
                      </Box>
                    )}

                    {/* Saved Entities - global bank */}
                    {(savedEntities[et.value] || []).length > 0 && (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                          Saved Entities ({(savedEntities[et.value] || []).length})
                        </Typography>
                        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                            <TableHead>
                              <TableRow>
                                {et.entity_format === 'json' ? (
                                  <>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.65rem', py: 0.5, color: 'text.secondary', width: '40%' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.65rem', py: 0.5, color: 'text.secondary' }}>Metadata</TableCell>
                                  </>
                                ) : (
                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.65rem', py: 0.5, color: 'text.secondary' }}>Content</TableCell>
                                )}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(savedEntities[et.value] || []).map((entity) => (
                                <React.Fragment key={entity.id}>
                                  <TableRow
                                    sx={{ '&:last-child td': { borderBottom: 0 }, cursor: et.entity_format === 'json' ? 'pointer' : 'default' }}
                                    hover={et.entity_format === 'json' || undefined}
                                    onClick={et.entity_format === 'json' ? () => setExpandedEntity(prev => prev === entity.id ? null : entity.id) : undefined}
                                  >
                                    {et.entity_format === 'json' ? (
                                      <>
                                        <TableCell sx={{ fontSize: '0.7rem', py: 0.4 }}>{entity.name}</TableCell>
                                        <TableCell sx={{ fontSize: '0.65rem', py: 0.4, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {JSON.stringify(entity.metadata)}
                                        </TableCell>
                                      </>
                                    ) : (
                                      <TableCell sx={{ fontSize: '0.7rem', py: 0.75, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {entity.definition || entity.name}
                                      </TableCell>
                                    )}
                                  </TableRow>
                                  {et.entity_format === 'json' && expandedEntity === entity.id && (
                                    <TableRow>
                                      <TableCell colSpan={2} sx={{ py: 1, borderBottom: 0, bgcolor: 'action.hover' }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>{entity.name}</Typography>
                                        <Box component="pre" sx={{ fontSize: '0.7rem', fontFamily: 'monospace', m: 0, mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', bgcolor: 'background.paper', p: 1, borderRadius: 0.5 }}>
                                          {JSON.stringify(entity.metadata, null, 2)}
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </React.Fragment>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
                  )}
                </SortableEntityWrapper>
              );
            })}
              </SortableContext>
            </DndContext>

            {/* Bottom Save Buttons */}
            <Box sx={{ display: 'flex', gap: 1, mt: 2, position: 'sticky', bottom: 0, bgcolor: 'background.paper', py: 1 }}>
              <Button
                variant="outlined"
                onClick={() => handleSaveAll(false)}
                disabled={saving}
                fullWidth
              >
                Save All
              </Button>
              <Button
                variant="contained"
                endIcon={<NextIcon />}
                onClick={() => handleSaveAll(true)}
                disabled={saving}
                fullWidth
              >
                {saving ? 'Saving...' : 'Save All & Next'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Saved notification */}
      <Snackbar
        open={showSaved}
        autoHideDuration={1500}
        onClose={() => setShowSaved(false)}
        message="Saved"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      {/* Copied notification */}
      <Snackbar
        open={showCopied}
        autoHideDuration={1500}
        onClose={() => setShowCopied(false)}
        message="Copied"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Test run progress notification */}
      <Snackbar
        open={!!activeTestRun}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 8 }}
      >
        <Alert
          severity={
            activeTestRun?.status === 'completed' ? 'success' :
            activeTestRun?.status === 'failed' ? 'error' : 'info'
          }
          onClose={() => setActiveTestRun(null)}
          sx={{
            minWidth: 420,
            backgroundColor: 'rgba(30, 30, 30, 0.65)',
            backdropFilter: 'blur(12px)',
            color: 'white',
            '& .MuiAlert-message': { width: '100%' },
            '& .MuiAlert-icon': { color: 'rgba(255,255,255,0.9)' },
            '& .MuiTypography-root': { color: 'white' },
            '& .MuiLinearProgress-root': { backgroundColor: 'rgba(255,255,255,0.2)' },
            '& .MuiLinearProgress-bar': { backgroundColor: 'rgba(255,255,255,0.8)' },
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {activeTestRun?.status === 'completed' ? 'Release Complete!' :
               activeTestRun?.status === 'failed' ? 'Release Failed' :
               `Releasing ${activeTestRun?.entityTypeLabel} ${activeTestRun?.version}`}
            </Typography>
            {activeTestRun && activeTestRun.status !== 'completed' && activeTestRun.status !== 'failed' && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Running on test cases: {activeTestRun.completedPressReleases}/{activeTestRun.totalPressReleases}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(activeTestRun.completedPressReleases / activeTestRun.totalPressReleases) * 100}
                  sx={{ mt: 1 }}
                />
              </>
            )}
            {activeTestRun?.status === 'completed' && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                All {activeTestRun.totalPressReleases} test cases complete. Refresh to see results.
              </Typography>
            )}
          </Box>
        </Alert>
      </Snackbar>

      {/* Clear Extractions Confirmation */}
      <Dialog open={clearConfirmOpen} onClose={() => setClearConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          Clear All Extractions
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            This will delete all extraction outputs and evaluation notes for this press release. You can re-run them afterwards.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleClearExtractions} color="error" variant="contained" startIcon={<DeleteIcon />}>
            Clear All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Debug Modal */}
      {debugModalEntity && (
        <PromptDebugModal
          open={debugModalOpen}
          onClose={() => {
            setDebugModalOpen(false);
            setDebugModalEntity(null);
          }}
          entityType={debugModalEntity.entityType}
          entityTypeLabel={debugModalEntity.entityTypeLabel}
          currentVersion={debugModalEntity.version}
          promptText={debugModalEntity.promptTemplate}
          pressReleaseId={pressReleaseId || ''}
          pressReleaseTitle={pressReleaseInfo?.press_release_title || ''}
          pressReleaseBody={pressReleaseInfo?.press_release_body || ''}
          currentOutput={debugModalEntity.output}
          responseMetadata={debugModalEntity.responseMetadata}
          onVersionCreated={(newVersionId) => {
            // Optionally refresh the entity data after a new version is created
            console.log('New version created:', newVersionId);
          }}
          onReleaseStarted={handleReleaseStarted}
        />
      )}

    </Wrapper>
  );
}
